import { useState, useCallback, useRef, useEffect } from 'react';
import { clienteService } from '../services/clienteService';
import { ClienteCreado, ClienteDetalle, CreateContactoPayload, UpdateClientePayload, UpdateContactoPayload } from '../types';

export interface EmpresaForm {
  razonSocial: string;
  nit: string;
  actividadEconomicaId: number | null;
  granContribuyente: boolean;
  departamentoId: number | null;
  ciudadId: number | null;
  direccion: string;
}

export interface ContactoForm {
  id: string;
  nombres: string;
  apellidos: string;
  cargo: string;
  telefono: string;
  correoElectronico: string;
  esPrincipal: boolean;
}

export interface EmpresaErrors {
  razonSocial?: string;
  nit?: string;
  actividadEconomicaId?: string;
  departamentoId?: string;
  ciudadId?: string;
  direccion?: string;
}

export interface ContactoErrors {
  nombres?: string;
  telefono?: string;
  correoElectronico?: string;
}

const EMPRESA_INICIAL: EmpresaForm = {
  razonSocial: '',
  nit: '',
  actividadEconomicaId: null,
  granContribuyente: false,
  departamentoId: null,
  ciudadId: null,
  direccion: '',
};

function generarId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function validarEmpresaForm(empresa: EmpresaForm): EmpresaErrors {
  // SPEC-177 (RN-001/RN-002) — nit, actividadEconomicaId y dirección dejan de ser
  // obligatorios en cualquier modo; solo razón social, departamento y ciudad se validan.
  const errors: EmpresaErrors = {};
  if (!empresa.razonSocial.trim()) errors.razonSocial = 'Ingresa la razón social.';
  if (!empresa.departamentoId) errors.departamentoId = 'Selecciona un departamento.';
  if (!empresa.ciudadId) errors.ciudadId = 'Selecciona una ciudad.';
  return errors;
}

function validarContactoForm(
  contacto: Omit<ContactoForm, 'id'>,
  contactosExistentes: ContactoForm[],
  editandoId?: string
): ContactoErrors {
  const errors: ContactoErrors = {};
  if (!contacto.nombres.trim()) errors.nombres = 'Ingresa el nombre del contacto.';
  if (!contacto.telefono.trim()) {
    errors.telefono = 'Ingresa un teléfono válido.';
  } else if (!/^\+?[0-9\s]{7,18}$/.test(contacto.telefono.trim())) {
    errors.telefono = 'Ingresa un teléfono válido.';
  }
  if (!contacto.correoElectronico.trim()) {
    errors.correoElectronico = 'Ingresa un correo electrónico válido.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contacto.correoElectronico.trim())) {
    errors.correoElectronico = 'Ingresa un correo electrónico válido.';
  } else {
    const correoNorm = contacto.correoElectronico.trim().toLowerCase();
    const duplicado = contactosExistentes.some(
      (c) => c.correoElectronico.toLowerCase() === correoNorm && c.id !== editandoId
    );
    if (duplicado) errors.correoElectronico = 'Este correo ya está registrado en otro contacto.';
  }
  return errors;
}

export function useNuevoCliente(
  open: boolean,
  onClose: () => void,
  onCreado?: (cliente?: ClienteCreado) => void,
  clienteEditar?: ClienteDetalle | null,
  onActualizado?: () => void,
) {
  const [empresa, setEmpresa] = useState<EmpresaForm>(EMPRESA_INICIAL);
  const [contactos, setContactos] = useState<ContactoForm[]>([]);
  const [editorAbierto, setEditorAbierto] = useState(false);
  const [contactoEditando, setContactoEditando] = useState<ContactoForm | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [empresaErrors, setEmpresaErrors] = useState<EmpresaErrors>({});
  const [contactoErrors, setContactoErrors] = useState<ContactoErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmDescartar, setConfirmDescartar] = useState(false);

  const isDirty = useRef(false);
  const contactosOriginalIdsRef = useRef<Set<string>>(new Set());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      setEditorAbierto(false);
      setContactoEditando(null);
      setIsSubmitting(false);
      setEmpresaErrors({});
      setContactoErrors({});
      setSubmitError(null);
      setConfirmDescartar(false);
      isDirty.current = false;

      if (clienteEditar) {
        setEmpresa({
          razonSocial: clienteEditar.razonSocial,
          nit: clienteEditar.nit ?? '',
          actividadEconomicaId: clienteEditar.actividadEconomicaId,
          granContribuyente: clienteEditar.granContribuyente,
          departamentoId: clienteEditar.departamentoId,
          ciudadId: clienteEditar.ciudadId,
          direccion: clienteEditar.direccion ?? '',
        });
        const contactosMapeados: ContactoForm[] = clienteEditar.contactos.map((c) => ({
          id: c.id!,
          nombres: c.nombres,
          apellidos: c.apellidos,
          cargo: c.cargo ?? '',
          telefono: c.telefono,
          correoElectronico: c.correoElectronico,
          esPrincipal: c.esPrincipal ?? false,
        }));
        setContactos(contactosMapeados);
        contactosOriginalIdsRef.current = new Set(contactosMapeados.map((c) => c.id));
      } else {
        setEmpresa(EMPRESA_INICIAL);
        setContactos([]);
        contactosOriginalIdsRef.current = new Set();
      }
    }
  }, [open, clienteEditar?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const marcarDirty = useCallback(() => {
    isDirty.current = true;
  }, []);

  const intentarCerrar = useCallback(() => {
    if (isDirty.current) {
      setConfirmDescartar(true);
    } else {
      onClose();
    }
  }, [onClose]);

  const confirmarDescartar = useCallback(() => {
    isDirty.current = false;
    onClose();
  }, [onClose]);

  const cancelarDescartar = useCallback(() => {
    setConfirmDescartar(false);
  }, []);

  const actualizarEmpresa = useCallback((campo: keyof EmpresaForm, valor: EmpresaForm[keyof EmpresaForm]) => {
    setEmpresa((prev) => {
      const next = { ...prev, [campo]: valor };
      if (campo === 'departamentoId') next.ciudadId = null;
      return next;
    });
    setEmpresaErrors((prev) => ({ ...prev, [campo]: undefined }));
    marcarDirty();
  }, [marcarDirty]);

  const abrirEditor = useCallback((contacto?: ContactoForm) => {
    setContactoEditando(contacto ?? null);
    setContactoErrors({});
    setEditorAbierto(true);
    marcarDirty();
  }, [marcarDirty]);

  const cerrarEditor = useCallback(() => {
    setEditorAbierto(false);
    setContactoEditando(null);
    setContactoErrors({});
  }, []);

  // SPEC-177 (RN-005) — se elimina el concepto de contacto principal de la UI: todos
  // los contactos se crean/editan con esPrincipal: false, sin diálogo de confirmación.
  const agregarContacto = useCallback((datos: Omit<ContactoForm, 'id'>): boolean => {
    const errors = validarContactoForm(datos, contactos);
    if (Object.keys(errors).length > 0) {
      setContactoErrors(errors);
      return false;
    }

    setContactos((prev) => [...prev, { ...datos, esPrincipal: false, id: generarId() }]);
    cerrarEditor();
    return true;
  }, [contactos, cerrarEditor]);

  const editarContacto = useCallback((datos: ContactoForm): boolean => {
    const errors = validarContactoForm(datos, contactos, datos.id);
    if (Object.keys(errors).length > 0) {
      setContactoErrors(errors);
      return false;
    }

    setContactos((prev) => prev.map((c) => (c.id === datos.id ? { ...datos, esPrincipal: false } : c)));
    cerrarEditor();
    return true;
  }, [contactos, cerrarEditor]);

  const eliminarContacto = useCallback((id: string) => {
    setContactos((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const modoEdicion = !!clienteEditar;

  const submit = useCallback(async () => {
    const errors = validarEmpresaForm(empresa);
    if (Object.keys(errors).length > 0) {
      setEmpresaErrors(errors);
      return;
    }
    if (contactos.length === 0) {
      setSubmitError('Debes agregar al menos un contacto.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (modoEdicion && clienteEditar) {
        const empresaPayload: UpdateClientePayload = {
          razonSocial: empresa.razonSocial.trim(),
          nit: empresa.nit.trim() || undefined,
          actividadEconomicaId: empresa.actividadEconomicaId ?? undefined,
          granContribuyente: empresa.granContribuyente,
          departamentoId: empresa.departamentoId!,
          ciudadId: empresa.ciudadId!,
          direccion: empresa.direccion.trim() || undefined,
        };
        await clienteService.actualizar(clienteEditar.id, empresaPayload);

        const currentRealIds = new Set(
          contactos.filter((c) => !c.id.startsWith('tmp-')).map((c) => c.id)
        );

        for (const c of contactos.filter((c) => c.id.startsWith('tmp-'))) {
          const payload: CreateContactoPayload = {
            nombres: c.nombres,
            apellidos: c.apellidos,
            cargo: c.cargo || undefined,
            telefono: c.telefono,
            correoElectronico: c.correoElectronico,
            esPrincipal: c.esPrincipal,
          };
          await clienteService.agregarContacto(clienteEditar.id, payload);
        }

        for (const c of contactos.filter((c) => !c.id.startsWith('tmp-'))) {
          const payload: UpdateContactoPayload = {
            nombres: c.nombres,
            apellidos: c.apellidos,
            cargo: c.cargo || undefined,
            telefono: c.telefono,
            correoElectronico: c.correoElectronico,
          };
          await clienteService.actualizarContacto(clienteEditar.id, c.id, payload);
        }

        const deletedIds = Array.from(contactosOriginalIdsRef.current).filter(
          (oid) => !currentRealIds.has(oid)
        );
        for (const oid of deletedIds) {
          await clienteService.eliminarContacto(clienteEditar.id, oid);
        }

        onClose();
        onActualizado?.();
      } else {
        const payload = {
          razonSocial: empresa.razonSocial.trim(),
          nit: empresa.nit.trim() || undefined,
          actividadEconomicaId: empresa.actividadEconomicaId ?? undefined,
          granContribuyente: empresa.granContribuyente,
          departamentoId: empresa.departamentoId!,
          ciudadId: empresa.ciudadId!,
          direccion: empresa.direccion.trim() || undefined,
          contactos: contactos.map((c): CreateContactoPayload => ({
            nombres: c.nombres,
            apellidos: c.apellidos,
            cargo: c.cargo || undefined,
            telefono: c.telefono,
            correoElectronico: c.correoElectronico,
            esPrincipal: c.esPrincipal,
          })),
        };
        const clienteCreado = await clienteService.crear(payload);
        onClose();
        onCreado?.({ id: clienteCreado.id, nit: clienteCreado.nit, razonSocial: clienteCreado.razonSocial, ciudad: clienteCreado.ciudad });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : modoEdicion ? 'Error al guardar cliente.' : 'Error al crear cliente.';
      if (message.includes('NIT')) {
        setEmpresaErrors((prev) => ({ ...prev, nit: message }));
      } else {
        setSubmitError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [empresa, contactos, modoEdicion, clienteEditar, onClose, onCreado, onActualizado]);

  return {
    empresa,
    contactos,
    editorAbierto,
    contactoEditando,
    isSubmitting,
    empresaErrors,
    contactoErrors,
    submitError,
    confirmDescartar,
    isDirty: isDirty.current,
    modoEdicion,
    intentarCerrar,
    confirmarDescartar,
    cancelarDescartar,
    actualizarEmpresa,
    abrirEditor,
    cerrarEditor,
    agregarContacto,
    editarContacto,
    eliminarContacto,
    submit,
  };
}
